import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Check, ArrowRight, Heart, Users, Calendar, Sparkles, 
  BookOpen, Video, MessageCircle, Gift, Shield, Clock, ArrowLeft
} from 'lucide-react';
import Footer from '@/components/layout/Footer';

// Images
const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_1d2f3656-52d0-4fe2-8a37-9f88482f6558/artifacts/b82ll32g_black%20women%20in%20IC%201.jpeg',
  community: 'https://customer-assets.emergentagent.com/job_1d2f3656-52d0-4fe2-8a37-9f88482f6558/artifacts/sw4k5hlz_black%20women%20in%20IC%202.jpeg',
  testimonials: 'https://customer-assets.emergentagent.com/job_1d2f3656-52d0-4fe2-8a37-9f88482f6558/artifacts/1g7pxgm1_black%20women%20in%20IC%203.jpeg',
  cta: 'https://customer-assets.emergentagent.com/job_1d2f3656-52d0-4fe2-8a37-9f88482f6558/artifacts/vqbgtarc_black%20weomen%20in%20ic%204.jpeg'
};

const InnerCirclePage = () => {
  const ontraportContainerRef = useRef(null);

  // Load Ontraport form script
  useEffect(() => {
    if (ontraportContainerRef.current) {
      // Clear any existing content
      ontraportContainerRef.current.innerHTML = '';
      
      // Create and append the Ontraport script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://app.ontraport.com/js/ontraport/opt_assets/drivers/opf.js';
      script.setAttribute('data-opf-uid', 'p2c260712f39');
      script.setAttribute('data-opf-params', 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1192&formWidth=100%&popPosition=mc&instance=126195367');
      
      ontraportContainerRef.current.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      if (ontraportContainerRef.current) {
        ontraportContainerRef.current.innerHTML = '';
      }
    };
  }, []);

  const features = [
    {
      icon: BookOpen,
      title: "Weekly Soul Food",
      description: "Audio and text wisdom to nourish your spirit throughout the week"
    },
    {
      icon: MessageCircle,
      title: "Weekly Reflection Prompts",
      description: "Thoughtful questions that invite you back to yourself"
    },
    {
      icon: Video,
      title: "Monthly Live Calls + Replays",
      description: "Connect with Crystal, Dr. Nicole, and your sisters in real-time"
    },
    {
      icon: Users,
      title: "Community Feed Access",
      description: "Share, connect, and grow with like-minded women"
    },
    {
      icon: BookOpen,
      title: "Curated Resource Library",
      description: "Access our growing collection of transformational content"
    },
    {
      icon: Sparkles,
      title: "Pathways to Deeper Work",
      description: "First access to retreats, coaching programs, and exclusive offerings"
    }
  ];

  const bonuses = [
    {
      icon: Gift,
      title: "First Month FREE",
      description: "Try Inner Circle for 30 days at no cost. Experience the transformation before you invest."
    },
    {
      icon: Shield,
      title: "No Obligation",
      description: "Cancel anytime with no questions asked. We believe you should stay because you want to, not because you have to."
    },
    {
      icon: Clock,
      title: "Flexible Membership",
      description: "After your free month, just $97/month. Pause or cancel whenever life needs you elsewhere."
    }
  ];

  const testimonials = [
    {
      quote: "Inner Circle gave me permission to finally put myself first. The community feels like coming home.",
      author: "Michelle T.",
      role: "Inner Circle Member"
    },
    {
      quote: "The weekly prompts alone have transformed how I show up in my life and business.",
      author: "Keisha R.",
      role: "Inner Circle Member"
    },
    {
      quote: "I've never felt so seen and supported. These women get it.",
      author: "Danielle M.",
      role: "Inner Circle Member"
    }
  ];

  const perfectFor = [
    "Stop carrying everyone else and finally put yourself first",
    "Feel deeply connected to women who truly understand your journey",
    "Wake up with clarity, purpose, and peace instead of anxiety",
    "Build unshakeable confidence that comes from the inside out",
    "Create a life you're proud of without burning out or selling out"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Back to Home Link - adjusted for mobile */}
      <div className="absolute top-2 left-4 sm:top-20 sm:left-6 z-20">
        <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white transition-colors text-sm sm:text-base">
          <ArrowLeft className="h-4 w-4" />
          <span className="font-medium">Back to Home</span>
        </Link>
      </div>

      {/* Hero Section with Embedded Payment */}
      <section className="relative text-white overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0">
          <img 
            src={IMAGES.hero} 
            alt="Sisters in community" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(44, 74, 62, 0.95), rgba(44, 74, 62, 0.80))' }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-10 sm:pt-16 md:py-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left: Value Proposition */}
            <div className="pt-8 sm:pt-4 lg:pt-8">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-4">
                <Gift className="h-4 w-4 text-terracotta" />
                <span className="text-sm font-medium">First Month FREE - Limited Time</span>
              </div>
              
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4">
                Inner Circle Membership
              </h1>
              
              <p className="text-lg md:text-xl text-white/90 mb-6 leading-relaxed">
                Become the woman who walks in her power, speaks her truth, and builds a life 
                she doesn&apos;t need to escape from. Surrounded by sisters who see you, hold you, 
                and remind you who you really are.
              </p>

              {/* Key Benefits */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-terracotta flex-shrink-0" />
                  <span className="text-white/90">Weekly soul food &amp; reflection prompts</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-terracotta flex-shrink-0" />
                  <span className="text-white/90">Monthly live calls with Crystal &amp; Dr. Nicole</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-terracotta flex-shrink-0" />
                  <span className="text-white/90">Private community of supportive sisters</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-terracotta flex-shrink-0" />
                  <span className="text-white/90">Cancel anytime - no commitment required</span>
                </div>
              </div>

              <p className="text-white/60 text-sm">
                Then just $97/month after your free trial
              </p>
            </div>

            {/* Right: Ontraport Embed Form - Scrollable container */}
            <div 
              className="rounded-2xl shadow-2xl overflow-hidden bg-white relative"
              style={{ width: '385px' }}
            >
              <div 
                ref={ontraportContainerRef}
                data-testid="ontraport-form-container"
                className="w-full overflow-y-auto bg-white"
                style={{ 
                  maxHeight: '650px', 
                  minHeight: '500px',
                  width: '420px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Free Month Highlight */}
      <section className="py-16 bg-gradient-to-r from-terracotta/10 via-terracotta/5 to-terracotta/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-2 border-terracotta/20">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="h-24 w-24 bg-terracotta/10 rounded-full flex items-center justify-center">
                  <Gift className="h-12 w-12 text-terracotta" />
                </div>
              </div>
              <div className="text-center md:text-left">
                <h2 className="font-heading text-2xl md:text-3xl text-forest mb-3">
                  Your First Month is Completely FREE
                </h2>
                <p className="text-gray-600 text-lg mb-4">
                  We want you to experience the transformation before you invest a single dollar. 
                  Try everything Inner Circle has to offer for 30 days - the community, the content, the connection.
                </p>
                <p className="text-forest font-medium">
                  No credit card tricks. No hidden fees. Just transformation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-terracotta font-medium text-sm uppercase tracking-wide">Everything You Get</span>
            <h2 className="font-heading text-3xl md:text-4xl text-forest mt-3 mb-4">
              What's Included in Inner Circle
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Everything you need to become the woman you were always meant to be
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-sand hover:border-forest/30 transition-colors">
                <CardContent className="p-6">
                  <div className="h-12 w-12 bg-forest/10 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-forest" />
                  </div>
                  <h3 className="font-heading text-lg text-forest mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Community Image Section */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src={IMAGES.community} 
              alt="Sisters supporting each other" 
              className="w-full object-cover object-center"
              style={{ height: '550px', objectPosition: 'center 40%' }}
            />
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            {/* Text content */}
            <div className="absolute inset-0 flex items-end justify-center">
              <div className="p-8 md:p-12 text-center w-full">
                <p className="text-2xl md:text-4xl lg:text-5xl font-heading text-white drop-shadow-lg" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}>
                  "This is where exhale meets sisterhood."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For You If */}
      <section className="py-20 bg-sand/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl text-forest mb-6">
                This is for You If You&apos;re Ready to...
              </h2>
              <div className="space-y-4">
                {perfectFor.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="h-6 w-6 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-terracotta" />
                    </div>
                    <p className="text-gray-700 text-lg">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={IMAGES.testimonials} 
                alt="Women celebrating transformation" 
                className="rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Promise */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {bonuses.map((bonus, index) => (
              <div key={index} className="text-center">
                <div className="h-16 w-16 bg-forest/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <bonus.icon className="h-8 w-8 text-forest" />
                </div>
                <h3 className="font-heading text-xl text-forest mb-2">{bonus.title}</h3>
                <p className="text-gray-600">{bonus.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 text-white" style={{ backgroundColor: '#2C4A3E' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl mb-4">
              What Our Sisters Are Saying
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 border-white/20">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Heart key={i} className="h-4 w-4 fill-terracotta text-terracotta" />
                    ))}
                  </div>
                  <p className="text-white/90 mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-medium text-white">{testimonial.author}</p>
                    <p className="text-white/60 text-sm">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-sand via-white to-sand relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={IMAGES.cta} 
                alt="Sister celebrating her transformation" 
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
            <div>
              <Heart className="h-10 w-10 text-terracotta mb-4" />
              <h2 className="font-heading text-3xl md:text-4xl text-forest mb-4">
                Your Transformation Starts Here
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                Join hundreds of women who stopped waiting for permission, chose themselves, 
                and became the women they always knew they could be.
              </p>

              <div className="bg-white rounded-2xl p-6 shadow-xl border border-sand mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xl font-heading text-forest">Inner Circle Membership</p>
                    <p className="text-gray-500">
                      <span className="line-through">$97/month</span>
                      <span className="text-terracotta font-bold ml-2">FREE for 30 days</span>
                    </p>
                    <p className="text-sm text-gray-400 mt-1">Then $97/month · Cancel anytime</p>
                  </div>
                  <Button 
                    size="lg" 
                    className="bg-terracotta hover:bg-terracotta/90 text-white h-14 px-8 text-lg whitespace-nowrap"
                    data-testid="final-join-inner-circle-btn"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Start Free Month <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>

              <p className="text-gray-500 text-sm">
                Questions? <a href="mailto:support@sisterdiamonds.com" className="text-forest hover:text-terracotta underline">Contact us</a> - we&apos;re here to help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Back Link */}
      <div className="bg-sand/50 py-6">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Link to="/" className="text-forest hover:text-terracotta transition-colors">
            ← Back to Sister Diamonds
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InnerCirclePage;
